import { useState, useMemo, useEffect } from "react";
import {
  Box,
  IconButton,
  Skeleton,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import FeaturedArticle from "../ArticleComponents/FeaturedArticle.js";
import CarouselDots from "./CarouselDots.js";
import CycleProgress from "./CycleProgress.js";

/* ---------------------------------------------
   Helpers
---------------------------------------------- */
const mod = (n, m) => (m === 0 ? 0 : ((n % m) + m) % m);

export default function HeroCarousel(props = {}) {

  const {
    articles = [],
    loading,
    cycleDurationMs
  } = props;

  const [index, setIndex] = useState(0);
  const [hovering, setHovering] = useState(false);
  const [cycleProgress, setCycleProgress] = useState(0)
  const [renderIndex, setRenderIndex] = useState(0);
  const [direction, setDirection] = useState(0); // -1 | 1
  const [animating, setAnimating] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0)

  const count = articles.length;

  // Animation configs
  const ANIMATION_DURATION_MS = 300
  const EASE_FORMULA = 'cubic-bezier(0.22, 1, 0.36, 1)'
  // 'cubic-bezier(0.4, 0, 0.2, 1)'
  const CENTER_SCALE = 'scale(1)'
  const PEEK_SCALE = 'scale(0.95)'

  /* ---------------------------------------------
     Windowed articles (prev | current | next)
  ---------------------------------------------- */

  // NEXT (direction === 1)
  // Phase 1 - -1: 0%   | 0: 15%  | 1: 70% | 2: 70%
  // Phase 2 - -1: 15%  | 0: 70%  | 1: 15% | 2: 15%
  // Phase 3 -            0: 15%  | 1: 70% | 2: 15%
  // Phase 4 -            0: 15%  | 1: 70% | 2: 15%
  // Phase 0 -            0: 15%  | 1: 70% | 2: 15%

  // PREV (direction === -1)
  // Phase 1 -  0: 15%  | 1: 70%  | 2: 15% | 3: 0%
  // Phase 2 -  0: 0%   | 1: 15%  | 2: 70% | 3: 15%
  // Phase 3 -  0: 15%  | 1: 70%  | 2: 15%
  // Phase 4 -  0: 15%  | 1: 70%  | 2: 15%
  // Phase 0 -  0: 15%  | 1: 70%  | 2: 15%

  const windowArticles = useMemo(() => {
    if (count < 3) return // throw new Error('TODO: support < 3 articles')

    const arts = [
      [
        articles[mod(renderIndex - 1, count)],
        {
          position: 0,
          flexDirection:
            direction === 1 ? 'row-reverse' : 'row',
          width:
            (animationPhase === 2 && direction === 1) ? '0%'
              : (animationPhase === 2 && direction === -1) ? '70%'
                : '15%',
          scale:
            (animationPhase === 2 && direction === -1) ? CENTER_SCALE
              : PEEK_SCALE,
          isCenter: direction === 1 ? false : [1, 2].includes(animationPhase)
        }
      ],
      [
        articles[renderIndex],
        {
          position: 1,
          flexDirection:
            direction === 1 ? 'row-reverse' : 'row',
          width:
            (animationPhase === 2 && direction === 1) ? '15%'
              : (animationPhase === 2 && direction === -1) ? '15%'
                : '70%',
          scale:
            (animationPhase === 2) ? PEEK_SCALE
              : CENTER_SCALE,
          isCenter: (animationPhase === 2 && direction === 1) ? false
            :  (animationPhase === 2 && direction === -1) ? false
            : true
        }
      ],
      [
        articles[mod(renderIndex + 1, count)],
        {
          position: 2,
          flexDirection:
            direction === 1 ? 'row-reverse' : 'row',
          width:
            (animationPhase === 2 && direction === 1) ? '70%'
              : (animationPhase === 1 && direction === -1) ? '70%'
                : '15%',
          scale:
            (animationPhase === 2 && direction === 1) ? CENTER_SCALE
              : PEEK_SCALE,
          isCenter: direction === -1 ? false : [1, 2].includes(animationPhase)
            
        }
      ],
    ];
    if ([1, 2].includes(animationPhase)) {
      if (direction === 1) {
        arts.push([
          articles[mod(renderIndex + 2, count)],
          {
            position: 3,
            flexDirection:
              direction === 1 ? 'row-reverse' : 'row',
            width:
              (animationPhase === 2) ? '15%'
                : '0%',
            scale: PEEK_SCALE,
            isCenter: false
          }
        ])
      }
      if (direction === -1) {
        arts.unshift([
          articles[mod(renderIndex - 2, count)],
          {
            position: -1,
            flexDirection:
              direction === 1 ? 'row-reverse' : 'row',
            width:
              animationPhase === 2 ? '15%'
                : '0%',
            scale: PEEK_SCALE,
            isCenter: false
          }
        ])
      }
    }
    return arts
  }, [articles, renderIndex, count, animationPhase, direction]);

  useEffect(() => {
    if (animationPhase === 1) setTimeout(() => setAnimationPhase(2), 10)
    if (animationPhase === 2) setTimeout(() => {
      setAnimationPhase(3)
      if (direction === -1) {
        const prev = mod(index - 1, count);
        setIndex(prev);
        setRenderIndex(prev);
      }
      if (direction === 1) {
        const next = mod(index + 1, count);
        setIndex(next);
        setRenderIndex(next);
      }
    }, ANIMATION_DURATION_MS)
    if (animationPhase === 3) setTimeout(() => setAnimationPhase(4), ANIMATION_DURATION_MS)
    if (animationPhase === 4) {
      setAnimationPhase(0)
      setDirection(0);
      setAnimating(false)
    }
  }, [animationPhase, index, count, direction])
  /* ---------------------------------------------
     Navigation
  ---------------------------------------------- */

  const goPrev = () => {
    if (animating) return

    setDirection(-1);
    setAnimating(true);
    setAnimationPhase(1);
  };

  const goNext = async () => {
    if (animating) return

    setDirection(1);
    setAnimating(true);
    setAnimationPhase(1)
  };

  useEffect(() => {
    if (cycleDurationMs > 0 && !hovering && !animating) {
      const step = 1 / 100;
      const autoCycleInterval = setInterval(() => {
        setCycleProgress(prev => {
          if (prev > 1) {
            goNext();
            return 0
          }
          return prev + step;
        });
      }, cycleDurationMs / 100);
      return () => {
        setCycleProgress(0)
        clearInterval(autoCycleInterval)
      };
    }
  }, [animating, hovering]);

  /* ---------------------------------------------
     Render
  ---------------------------------------------- */
  return (
    <>

      {/* OUTER WRAPPER — always full width */}
      <Box
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        sx={{ width: "100%", py: 2, position: "relative", height: '100%' }}
      >
        {/* INNER WRAPPER — controls content width */}
        <Box
          sx={{
            width: "100%",
            height: '100%',
            mx: "auto",
            position: "relative",
          }}
        >

          {/* Carousel */}
          <Box sx={{ width: '100%', position: "relative", overflow: "hidden", height: '100%', }}>
            {/* Navigation */}
            {count > 1 && (
              <>
                <IconButton
                  onClick={goPrev}
                  sx={{
                    position: "absolute",
                    left: 32,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 10,
                    backgroundColor: "rgba(0,0,0,0.4)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.6)",
                    },
                  }}
                >
                  <ChevronLeft fontSize="large" />
                </IconButton>

                <IconButton
                  onClick={goNext}
                  sx={{
                    position: "absolute",
                    right: 32,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 10,
                    backgroundColor: "rgba(0,0,0,0.4)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.6)",
                    },
                  }}
                >
                  <ChevronRight fontSize="large" />
                </IconButton>
              </>
            )}

            {loading ? (
              <Skeleton
                variant="rectangular"
                sx={{ height: 420, borderRadius: 2 }}
              />
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", height: '100%', }}>
                {/* Track */}
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    height: '100%',
                  }}
                >
                  {windowArticles?.map((articleConfig) => {
                    const [article, { isCenter, width, flexDirection, scale }] = articleConfig
                    return (
                      <Box
                        key={article.guid}
                        sx={{
                          width,
                          height: '100%',
                          flexShrink: 0,
                          flexDirection,
                          px: width === '15%' ? 2 : 0,
                          opacity: isCenter ? 1 : 0.4,
                          transform: scale,
                          transition:
                            `all ${ANIMATION_DURATION_MS}ms ${EASE_FORMULA}`,
                          pointerEvents: isCenter
                            ? "auto"
                            : "none",
                        }}
                      >

                        <Box
                          sx={{
                            height: '100%',
                            borderRadius: 2,
                            overflow: "hidden",
                            textAlign: 'left',
                            verticalAlign: 'top'
                          }}>
                          <FeaturedArticle
                            article={article}
                            sitePath={article.sitePath + '/news'}
                            fitImage={!isCenter}
                            showInfo={isCenter}
                            enableTags
                            controls={isCenter && !hovering && <CycleProgress progress={cycleProgress} />}
                          />
                        </Box>

                      </Box>
                    );
                  })}
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 5,
                  }}
                >
                  <CarouselDots
                    count={count}
                    activeIndex={index}
                    onSelect={(i) => {
                      if (i === index) return;
                      if (i > index) {
                        goNext()
                      } else {
                        goPrev()
                      }
                    }}
                  />
                  
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}